/**
 * Shared registry of `apps/sample-data` themes.
 *
 * Lives in `@repo/shared` so all three consumers — the sample-data writer
 * service, the API (which spins up read-only datasources pointing at these
 * databases), and the web app (which renders the onboarding showcase) —
 * agree on the canonical id, database name, and display copy for each
 * theme. Adding a new theme means adding an entry here and wiring the
 * runtime concerns (`schemaModule`, `seed`, `appendDay`) inside
 * `apps/sample-data/src/themes/<id>`.
 */

/** Ordered list of every shipped sample-data theme id. */
export const sampleDataThemeIds = ["ecommerce", "saas", "marketplace"] as const;

/** Identifier for each shipped sample-data theme. */
export type SampleDataThemeId = (typeof sampleDataThemeIds)[number];

/** Bullet-point copy used inside the onboarding sample showcase. */
export interface SampleDataThemeHighlight {
  /** Base icon name from `@repo/icons/base`. */
  icon: string;
  title: string;
  description: string;
}

/**
 * Display + connection metadata for a sample-data theme. Runtime fields
 * (Drizzle schema module, faker generators) live alongside the theme in
 * `apps/sample-data` and reference this metadata for `id` / `databaseName`.
 */
export interface SampleDataThemeMeta {
  id: SampleDataThemeId;
  /** Postgres database on the shared sample-data host that owns this theme's tables. */
  databaseName: string;
  /** Human-readable theme name (used as the datasource name + showcase title). */
  displayName: string;
  /** One-sentence intro shown in the onboarding showcase. */
  description: string;
  /** Bullet-point feature list shown in the onboarding showcase. */
  highlights: SampleDataThemeHighlight[];
}

export const sampleDataThemes: Record<SampleDataThemeId, SampleDataThemeMeta> =
  {
    ecommerce: {
      id: "ecommerce",
      databaseName: "ecommerce",
      displayName: "E-commerce",
      description:
        "A realistic e-commerce warehouse with customers, products, orders, and a high-volume event stream.",
      highlights: [
        {
          icon: "person",
          title: "Customers & orders",
          description:
            "Daily customer cohorts, ~6–10% session-to-order conversion, refunds and cancellations included.",
        },
        {
          icon: "dataset",
          title: "Product catalog",
          description:
            "A stable catalog of ~240 products across categories, with cost and price for margin analysis.",
        },
        {
          icon: "bolt",
          title: "Sessions & events",
          description:
            "Intraday traffic with UTM attribution and a product-analytics event stream.",
        },
        {
          icon: "dashboard",
          title: "Ready for dashboards",
          description:
            "Yearly growth trend plus seasonality so charts and stats look like the real thing.",
        },
      ],
    },
    saas: {
      id: "saas",
      databaseName: "saas",
      displayName: "SaaS sample",
      description:
        "A B2B SaaS warehouse with accounts, plans, subscriptions, monthly invoices, and a feature-usage event stream.",
      highlights: [
        {
          icon: "person",
          title: "Accounts & seats",
          description:
            "B2B accounts with industry, size band, and country; owner plus invited seats per account.",
        },
        {
          icon: "dataset",
          title: "Plans & subscriptions",
          description:
            "A small plan catalog (Free through Enterprise, monthly + annual) and a current-state subscriptions table you can sum directly.",
        },
        {
          icon: "bolt",
          title: "Lifecycle & MRR",
          description:
            "Trials, conversions, upgrades, downgrades, churn, and reactivations as an append-only audit — perfect for MRR waterfalls.",
        },
        {
          icon: "dashboard",
          title: "Feature usage",
          description:
            "Daily feature events scaled to active seats so DAU / WAU / adoption charts have signal.",
        },
      ],
    },
    marketplace: {
      id: "marketplace",
      databaseName: "marketplace",
      displayName: "Marketplace sample",
      description:
        "A two-sided service marketplace with sellers, buyers, dynamic listings, bookings with date ranges, bi-directional reviews, and weekly seller payouts.",
      highlights: [
        {
          icon: "person",
          title: "Sellers & buyers",
          description:
            "Peer supply and demand sides as separate entity types so take rate, GMV, and dual-cohort retention charts work natively.",
        },
        {
          icon: "dataset",
          title: "Dynamic listings",
          description:
            "Seller-owned listings created and delisted over time \u2014 not a static catalog, so listing-level cohort analysis is meaningful.",
        },
        {
          icon: "bolt",
          title: "Booking lifecycle",
          description:
            "Requested \u2192 accepted \u2192 completed (or canceled / rejected) with stay date ranges and lead-time-to-booking distributions.",
        },
        {
          icon: "dashboard",
          title: "Reviews & payouts",
          description:
            "Bi-directional reviews and weekly seller payouts so take-rate, NPS, and host-earnings charts read like a real marketplace.",
        },
      ],
    },
  };

/** Default theme used by the onboarding "Skip for now" path. */
export const DEFAULT_SAMPLE_THEME_ID: SampleDataThemeId = "ecommerce";
